import { useState } from "react";
import { Check, ChevronDown } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { setPendingEditSource } from "./pendingEditSource";

/**
 * Cello — three-tier pricing section ("Choose your edit").
 * Drop-in replacement for <PricingBass /> on the splash pages.
 *
 * Renders the mobile layout under md and the desktop grid at md+.
 * All three edits are $160 one-time; add-ons start at $30.
 *
 * TODO before ship:
 *   - fill in checkoutUrl for each edit (Stripe payment links)
 *   - swap the Unsplash placeholders for owned photography and drop the credits
 *   - point the footer CTA at whatever "CELLO EDIT" should do
 */

export interface EditDetail {
  /** Left-hand label, e.g. "Analysis" — keep the order consistent across edits. */
  label: string;
  /** Right-hand specifics for this edit. */
  text: string;
}

export interface Edit {
  id: string;
  name: string;
  /** Short friendly name for the waitlist "Got it — the {label} edit" line;
   *  also what's stored in the edit_source column. */
  label: string;
  tagline: string;
  image: { src: string; alt: string; credit?: string; creditHref?: string };
  details: EditDetail[];
  addOn: string;
  cta: string;
  checkoutUrl?: string;
}

const PRICE = "$160";
const ADD_ON_FROM = "From $30";

/** Shared across all three edits — rendered once, above the cards. */
const INCLUDED = [
  "Wardrobe analysis",
  "Body scan + analysis",
  "Shopping recommendations",
  "Sample outfit combinations",
];

const BODY_SCAN = "5-min scan to get your measurements";

const EDITS: Edit[] = [
  {
    id: "capsule",
    name: "Capsule Wardrobe Build",
    label: "Capsule",
    tagline:
      "Build a wardrobe that’s easy to reach for and rotate for your daily routine",
    image: {
      src: "https://images.unsplash.com/photo-1504198458649-3128b932f49e?fm=jpg&q=70&w=900&auto=format&fit=crop",
      alt: "Folded neutral garments",
    },
    details: [
      { label: "Analysis", text: "Your existing wardrobe analyzed for gaps" },
      { label: "Body scan", text: BODY_SCAN },
      {
        label: "Shopping",
        text: "Selected pieces to help you round out a foundational capsule wardrobe that fits you, your lifestyle, and budget",
      },
      { label: "Outfits", text: "Sample combinations from your capsule" },
    ],
    addOn: "Additional outfit combos with your capsule wardrobe",
    cta: "Book This Edit",
  },
  {
    id: "upgrade",
    name: "The Thoughtful Upgrade",
    label: "Thoughtful Upgrade",
    tagline:
      "Integrate thoughtful new additions into your wardrobe on top of the base that’s already working",
    image: {
      src: "https://images.unsplash.com/photo-1602810318383-e386cc2a3ccf?fm=jpg&q=70&w=900&auto=format&fit=crop",
      alt: "Shirts folded on wood",
    },
    details: [
      { label: "Analysis", text: "Identify what’s working, and where to refresh" },
      { label: "Body scan", text: BODY_SCAN },
      {
        label: "Shopping",
        text: "Pieces that elevate the closet you own designed to fit you, your lifestyle, and budget",
      },
      { label: "Outfits", text: "New pieces styled with what you have" },
    ],
    addOn:
      "Additional outfit recommendations for events, special occasions, and daily life",
    cta: "Book This Edit",
  },
  {
    id: "new-build",
    name: "New Wardrobe Build",
    label: "New Wardrobe",
    tagline: "Reconstruct your wardrobe so your closet matches your life.",
    image: {
      src: "https://images.unsplash.com/photo-1453486030486-0a5ffcd82cd9?fm=jpg&q=70&w=900&auto=format&fit=crop",
      alt: "Full rail of hanging clothes",
    },
    details: [
      {
        label: "Analysis",
        text: "A full read of your closet to see what's missing",
      },
      { label: "Body scan", text: BODY_SCAN },
      {
        label: "Shopping",
        text: "A whole wardrobe chosen to flatter you and your lifestyle",
      },
      { label: "Outfits", text: "Combinations tailored to your life" },
    ],
    addOn:
      "Additional outfit recommendations for events, special occasions, and daily life",
    cta: "Book This Edit",
  },
];

interface PricingEditsProps {
  onJoinClick?: () => void;
  /** Which card is selected on load. Default: none. */
  defaultSelected?: string | null;
  /** Desktop: start every "What that means here" panel open. Default: true. */
  startExpanded?: boolean;
}

const AddOn = ({ text }: { text: string }) => (
  <div className="mt-2 flex items-baseline gap-2.5 rounded-[4px] border border-dashed border-[#e0d5c8] bg-[#fffaf5] px-3.5 py-2">
    <span className="shrink-0 text-[8.5px] font-bold uppercase tracking-[0.2em] text-[#c2410c]">Add on</span>
    <span className="flex-1 text-[11px] leading-[1.35] text-[#6B6449]">{text}</span>
    <span className="shrink-0 text-[8.5px] font-bold uppercase tracking-[0.14em] text-[#a39a89]">{ADD_ON_FROM}</span>
  </div>
);

const DetailRows = ({ details }: { details: EditDetail[] }) => (
  <div className="flex flex-col gap-[11px]">
    {details.map((d) => (
      <div key={d.label} className="flex items-baseline gap-3">
        <span className="w-[104px] flex-none text-[9px] font-bold uppercase tracking-[0.13em] text-[#a39a89]">
          {d.label}
        </span>
        <span className="flex-1 text-[13px] leading-[1.45] text-[#2a241c]">
          {d.text}
        </span>
      </div>
    ))}
  </div>
);

const Photo = ({ image, className }: { image: Edit["image"]; className?: string }) => (
  <div className={`relative w-full overflow-hidden bg-[#f3ece4] ${className ?? ""}`}>
    <img src={image.src} alt={image.alt} className="h-full w-full object-cover" loading="lazy" />
    {image.credit && (
      <a
        href={image.creditHref}
        target="_blank"
        rel="noopener noreferrer nofollow"
        onClick={(e) => e.stopPropagation()}
        className="absolute bottom-0 right-0 bg-black/45 px-2 py-1 text-[8px] leading-none text-white/80 hover:text-white"
      >
        {image.credit}
      </a>
    )}
  </div>
);

const PricingEdits = ({
  onJoinClick,
  defaultSelected = null,
  startExpanded = true,
}: PricingEditsProps = {}) => {
  const navigate = useNavigate();
  const [selected, setSelected] = useState<string | null>(defaultSelected);
  const [open, setOpen] = useState<string[]>(() => {
    // Mobile always starts collapsed (expanded accordions make the stacked
    // cards very tall); desktop honors startExpanded.
    const isDesktop =
      typeof window !== "undefined" &&
      window.matchMedia("(min-width: 768px)").matches;
    return isDesktop && startExpanded ? EDITS.map((e) => e.id) : [];
  });

  const isOpen = (id: string) => open.includes(id);
  const toggle = (id: string) =>
    setOpen((o) => (o.includes(id) ? o.filter((x) => x !== id) : [...o, id]));

  const pick = (id: string) => {
    setSelected((s) => (s === id ? null : id));
    if (!isOpen(id)) setOpen((o) => [...o, id]);
  };

  const book = (edit: Edit) => {
    if (edit.checkoutUrl) window.location.assign(edit.checkoutUrl);
    else if (onJoinClick) {
      // Stash which edit they booked so the shared waitlist dialog can call it
      // out and record it.
      setPendingEditSource(edit.label);
      onJoinClick();
    } else navigate("/auth", { state: { tab: "signup" } });
  };

  /* ---------------------------------------------------------------- mobile */
  const mobile = (
    <section className="bg-[#FFF7F0] px-6 py-[72px] md:hidden">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h2 className="mb-3 font-playfair text-[38px] font-bold leading-none text-[#0f172a]">
            Pricing
          </h2>
          <p className="font-playfair text-[14px] italic leading-[1.55] text-[#57534e]">
            Choose the edit that matches what you need. Can&apos;t decide? We can build a
            custom one for you during your call.
          </p>
        </div>
        <div className="flex-none text-right">
          <div className="font-playfair text-[40px] font-bold leading-none text-[#0f172a]">
            {PRICE}
          </div>
          <div className="mt-1 text-[9px] font-bold uppercase leading-[1.4] tracking-[0.15em]">
            <span className="text-[#c2410c]">Any option</span>
            <br />
            <span className="text-[#57534e]">One time</span>
          </div>
        </div>
      </div>

      <div className="mt-6 rounded-lg border border-[#ece6df] bg-white px-4 py-3.5">
        <div className="mb-2.5 text-[10px] font-bold uppercase tracking-[0.15em] text-[#0f172a]">
          All edits include
        </div>
        <div className="flex flex-wrap gap-x-4 gap-y-2">
          {INCLUDED.map((f) => (
            <span key={f} className="flex items-center gap-1.5 text-[12px] text-[#334155]">
              <span className="h-1.5 w-1.5 flex-none rounded-full bg-[#FF8B11]" />
              {f}
            </span>
          ))}
        </div>
      </div>

      <div className="mb-4 mt-7 flex items-center gap-3">
        <span className="whitespace-nowrap text-[10px] font-bold uppercase tracking-[0.2em] text-[#a8a29e]">
          Choose your edit
        </span>
        <span className="h-px flex-1 bg-[#e7ded3]" />
      </div>

      <div className="flex flex-col gap-4" role="radiogroup" aria-label="Choose your edit">
        {EDITS.map((edit) => {
          const picked = selected === edit.id;
          const expanded = isOpen(edit.id);
          const panelId = `edit-m-panel-${edit.id}`;
          return (
            <div
              key={edit.id}
              className={`overflow-hidden rounded-2xl bg-white shadow-[0_16px_40px_-24px_rgba(15,23,42,0.3)] ${
                picked ? "border-2 border-[#FF8B11]" : "border border-[#ece6df]"
              }`}
            >
              <Photo image={edit.image} className="aspect-video" />
              <div className="p-5">
                <button
                  type="button"
                  role="radio"
                  aria-checked={picked}
                  onClick={() => pick(edit.id)}
                  className="flex w-full items-start gap-3 text-left"
                >
                  <span
                    aria-hidden="true"
                    className={`mt-1 h-4 w-4 flex-none rounded-full ${
                      picked ? "border-[5px] border-[#FF8B11]" : "border-[1.5px] border-[#d6d3d1]"
                    }`}
                  />
                  <span className="font-playfair text-[22px] font-bold leading-[1.2] text-[#0f172a]">
                    {edit.name}
                  </span>
                </button>
                <p className="ml-7 mt-2.5 font-playfair text-[14px] leading-[1.55] text-[#57534e]">
                  {edit.tagline}
                </p>

                <button
                  type="button"
                  aria-expanded={expanded}
                  aria-controls={panelId}
                  onClick={() => toggle(edit.id)}
                  className="mt-[18px] flex min-h-[44px] w-full items-center justify-between border-y border-[#f1ede7] py-3 text-[10px] font-bold uppercase tracking-[0.15em] text-[#0f172a]"
                >
                  What that means here
                  <span aria-hidden="true" className="text-[14px] text-[#FF8B11]">{expanded ? "−" : "+"}</span>
                </button>
                {expanded && (
                  <div id={panelId} className="pt-4">
                    <div className="flex flex-col gap-3">
                      {edit.details.map((d) => (
                        <div key={d.label} className="text-[13.5px] leading-[1.65] text-[#334155]">
                          <span className="mr-2 text-[9px] font-bold uppercase tracking-[0.13em] text-[#a8a29e]">{d.label}</span>
                          {d.text}
                        </div>
                      ))}
                    </div>
                    <AddOn text={edit.addOn} />
                  </div>
                )}

                <button
                  type="button"
                  onClick={() => book(edit)}
                  className="mt-[18px] h-12 w-full rounded-md bg-[#3f3a1f] text-[11px] font-bold uppercase tracking-[0.15em] text-white shadow-[inset_0_-2px_0_#262312] active:translate-y-[2px] active:shadow-none"
                >
                  Book This Edit
                </button>
              </div>
            </div>
          );
        })}
      </div>

      <p className="mt-8 text-center font-playfair text-[15px] italic text-[#57534e]">
        Unsure which one? We&apos;ll help you decide.
      </p>
      <div className="mt-4 text-center">
        <button
          type="button"
          onClick={() => (onJoinClick ? onJoinClick() : navigate("/auth", { state: { tab: "signup" } }))}
          className="h-12 rounded-md border border-[#d6d3d1] bg-white px-7 text-[11px] font-bold uppercase tracking-[0.15em] text-[#0f172a]"
        >
          CELLO EDIT
        </button>
      </div>
    </section>
  );

  /* --------------------------------------------------------------- desktop */
  const desktop = (
    <section className="relative hidden overflow-hidden border-t border-[#e2d8ce] bg-[#FFF7F0] px-8 pt-[72px] pb-[96px] md:block">
      <div className="absolute inset-0 bg-[radial-gradient(#e2d8ce_1px,transparent_1px)] [background-size:24px_24px] opacity-60" />

      <div className="relative z-10 mx-auto max-w-[1200px]">
        <div className="flex flex-wrap items-end justify-between gap-10">
          <div className="max-w-[560px]">
            <h2 className="font-playfair text-[56px] font-bold leading-none tracking-tight text-[#0a0502]">
              Pricing
            </h2>
            <p className="mt-4 font-playfair text-[19px] italic leading-[1.45] text-[#6B6449]">
              Choose the edit that matches what you need. Can&apos;t decide? We can build a
              custom one for you during your call.
            </p>
          </div>
          <div className="flex items-baseline gap-3.5 pb-1.5">
            <span className="font-playfair text-[64px] font-bold leading-[0.9] text-[#0a0502]">
              {PRICE}
            </span>
            <span className="block text-[10px] font-bold uppercase tracking-[0.22em] text-[#c2410c]">
              Any option
              <br />
              <span className="text-[#8a8272]">One time</span>
            </span>
          </div>
        </div>

        <div className="mt-10 flex flex-wrap items-center gap-7 rounded-[6px] border border-[#e2d8ce] bg-[#fffaf5] p-[20px_24px]">
          <span className="flex-none text-[9.5px] font-bold uppercase tracking-[0.25em] text-[#0a0502]">
            All edits include
          </span>
          <div className="flex flex-1 flex-wrap gap-[26px]">
            {INCLUDED.map((f) => (
              <span key={f} className="flex items-center gap-2 text-[11.5px] text-[#2a241c]">
                <span className="flex h-[15px] w-[15px] flex-none items-center justify-center rounded-full bg-[#fdece2]">
                  <Check className="h-2 w-2 text-[#c2410c]" strokeWidth={3} />
                </span>
                {f}
              </span>
            ))}
          </div>
        </div>

        <div className="mb-6 mt-9 flex items-center gap-4">
          <span className="flex-none whitespace-nowrap text-[9.5px] font-bold uppercase tracking-[0.28em] text-[#a39a89]">
            Choose your edit
          </span>
          <span className="h-px flex-1 bg-[#e2d8ce]" />
        </div>

        <div className="grid grid-cols-[repeat(auto-fit,minmax(300px,1fr))] items-stretch gap-5">
          {EDITS.map((edit) => {
            const picked = selected === edit.id;
            return (
              <div
                key={edit.id}
                onClick={() => pick(edit.id)}
                className={`relative flex cursor-pointer flex-col overflow-hidden rounded-[6px] bg-white pb-[30px] transition-all ${
                  picked
                    ? "border-[1.5px] border-[#3D3A1F] shadow-[0_24px_60px_rgba(61,58,31,0.20)]"
                    : "border-[1.5px] border-[#e2d8ce] shadow-[0_20px_50px_rgba(0,0,0,0.06)]"
                }`}
              >
                <div className="relative">
                  <Photo image={edit.image} className="h-[190px]" />
                  <span
                    className={`pointer-events-none absolute right-3.5 top-3.5 z-[2] rounded-[2px] bg-[#c2410c] px-[11px] py-1.5 text-[8.5px] font-bold uppercase tracking-[0.2em] text-white transition-opacity ${
                      picked ? "opacity-100" : "opacity-0"
                    }`}
                  >
                    Selected
                  </span>
                </div>

                <div className="flex items-start gap-3 px-7 pt-[26px]">
                  <span
                    className={`mt-[5px] flex h-[18px] w-[18px] flex-none items-center justify-center rounded-full border-[1.5px] transition-colors ${
                      picked ? "border-[#c2410c]" : "border-[#cfc5b7]"
                    }`}
                  >
                    <span
                      className={`h-[9px] w-[9px] rounded-full transition-colors ${
                        picked ? "bg-[#c2410c]" : "bg-transparent"
                      }`}
                    />
                  </span>
                  <h3 className="font-playfair text-[26px] font-bold leading-[1.14] tracking-tight text-[#0a0502]">
                    {edit.name}
                  </h3>
                </div>

                <p className="mb-6 ml-[58px] mr-7 mt-3.5 font-playfair text-[14.5px] leading-[1.5] text-[#6B6449]">
                  {edit.tagline}
                </p>

                <div className="mx-7 mt-auto h-px bg-[#ede4da]" />

                <button
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    toggle(edit.id);
                  }}
                  className="mx-7 flex items-center justify-between gap-3 py-[17px] text-left"
                >
                  <span className="text-[9.5px] font-bold uppercase tracking-[0.25em] text-[#0a0502]">
                    What that means here
                  </span>
                  <span
                    className={`flex h-[22px] w-[22px] items-center justify-center rounded-full bg-[#fdece2] transition-transform duration-300 ${
                      isOpen(edit.id) ? "rotate-180" : ""
                    }`}
                  >
                    <ChevronDown className="h-3 w-3 text-[#c2410c]" />
                  </span>
                </button>

                <div
                  className="mx-7 overflow-hidden transition-all duration-500"
                  style={{
                    maxHeight: isOpen(edit.id) ? 640 : 0,
                    opacity: isOpen(edit.id) ? 1 : 0,
                  }}
                >
                  <div className="pb-1">
                    <DetailRows details={edit.details} />
                    <AddOn text={edit.addOn} />
                  </div>
                </div>

                <button
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    book(edit);
                  }}
                  className={`mx-7 mt-5 block py-[18px] text-center text-[10px] font-bold uppercase tracking-[0.25em] transition-colors hover:bg-[#c2410c] hover:text-white ${
                    picked ? "bg-[#c2410c] text-white" : "bg-[#3D3A1F] text-[#FFF7F0]"
                  }`}
                >
                  {edit.cta}
                </button>
              </div>
            );
          })}
        </div>

        <div className="mt-[60px] flex flex-col items-center gap-5 text-center">
          <p className="font-playfair text-[19px] italic text-[#6B6449]">
            Unsure which one? We&apos;ll help you decide.
          </p>
          <button
            type="button"
            onClick={() => (onJoinClick ? onJoinClick() : navigate("/auth", { state: { tab: "signup" } }))}
            className="rounded-[3px] border-[1.5px] border-[#403B1D] px-[46px] py-[18px] text-[12px] font-bold uppercase tracking-[0.2em] text-[#403B1D] transition-colors hover:bg-[#403B1D] hover:text-[#FAF1E8]"
          >
            CELLO EDIT
          </button>
        </div>
      </div>
    </section>
  );

  // #pricing is the footer's anchor; the scroll margin clears the mobile sticky header.
  return (
    <div id="pricing" className="scroll-mt-[var(--sticky-h,0px)] md:scroll-mt-0">
      {mobile}
      {desktop}
    </div>
  );
};

export default PricingEdits;
